import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { downloadBlob } from "@/lib/resumeUtils";
import {
  STATUS_KEYS, CHANNEL_KEYS, JOB_TYPE_KEYS,
  statusBadgeClass, statusLabelKey, channelLabelKey, jobTypeLabelKey, jobTypeShortLabel, formatDate,
} from "@/lib/constants";
import { Plus, Search, Trash2, Archive, ArchiveRestore, FileX2, Sparkles, Mail, Send, Paperclip, FileDown, CalendarCheck, ChevronLeft, ChevronRight, Compass, ArrowRight, CheckSquare, Square, X, FileText, FileSpreadsheet, Rocket, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import FollowUpDialog from "./FollowUpDialog";
import ThinkingLoader from "./ThinkingLoader";
import { PROMOTED_SOURCES } from "@/lib/jobSource";

const PAGE_SIZE = 10;

export default function JobsOverview({ jobs, loading, jobsView = "active", onChangeView, onOpenJob, onPrep, onLetter, onEmail, onDelete, onArchive, onUnarchive, onBulkArchive, onBulkUnarchive, onNewJob, onRefresh, onGoToJobSearch, onQuickApply }) {
  const { t, lang } = useT();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterChannel, setFilterChannel] = useState("all");
  const [filterType, setFilterType] = useState("all");
  // Sort mode. Default "recent" preserves the pre-existing date-applied desc
  // order; "match_desc" surfaces the strongest AI-match-insight scores first
  // so users can pounce on top fits without hunting.
  const [sortBy, setSortBy] = useState("recent");
  const [lettersByJob, setLettersByJob] = useState({});
  const [docsOpen, setDocsOpen] = useState(null);
  const [docsCache, setDocsCache] = useState({});
  const [dlBusy, setDlBusy] = useState(null);
  // First-time archive notice — shown once per browser via localStorage.
  // Persisted key means the user only sees the retention popup on their
  // very first click; from then on it opens the archive silently.
  const [archiveNotice, setArchiveNotice] = useState(false);
  // Which format is currently being generated (pdf|xlsx|null). Doubles as
  // a disable-all-buttons flag so users can't fire two exports in parallel.
  const [exportBusy, setExportBusy] = useState(null);

  const handleExportArchive = async (format) => {
    if (exportBusy) return;
    setExportBusy(format);
    try {
      const res = await api.get(`/jobs/archive/export?format=${format}&lang=${lang || "nl"}`, {
        responseType: "blob",
      });
      const today = new Date().toISOString().slice(0, 10);
      const fname = `jobgrid-archief-${today}.${format}`;
      downloadBlob(res.data, fname);
      toast.success(t("dashArchiveExportDone"));
    } catch {
      toast.error(t("dashArchiveExportFailed"));
    } finally {
      setExportBusy(null);
    }
  };

  const openDocs = async (jobId) => {
    if (docsOpen === jobId) {
      setDocsOpen(null);
      return;
    }
    setDocsOpen(jobId);
    if (!docsCache[jobId]) {
      try {
        const { data } = await api.get(`/jobs/${jobId}/attachments`);
        setDocsCache((c) => ({ ...c, [jobId]: data }));
      } catch {
        setDocsCache((c) => ({ ...c, [jobId]: { cv: { present: false }, letter: { present: false } } }));
      }
    }
  };

  const downloadJobCv = async (jobId) => {
    setDlBusy(`jobcv-${jobId}`);
    try {
      const res = await api.get(`/jobs/${jobId}/cv/download`, { responseType: "blob" });
      const fn = docsCache[jobId]?.cv?.filename || "job_cv.pdf";
      downloadBlob(new Blob([res.data]), fn);
    } catch {
      toast.error(t("ltDlResumeMissing"));
    } finally {
      setDlBusy(null);
    }
  };
  const [followUpJob, setFollowUpJob] = useState(null);
  const [page, setPage] = useState(1);
  // Selection state for bulk archive/unarchive. A Set of job ids currently
  // ticked in the table. Cleared whenever the view (active/archive) or the
  // active filters change so we never operate on a stale selection.
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Back to page 1 whenever the result set changes
  useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [search, filterStatus, filterChannel, filterType, sortBy, jobsView]);

  // Map job_id -> most recent letter (list is sorted newest first)
  useEffect(() => {
    api.get("/letters").then(({ data }) => {
      const map = {};
      for (const l of data) {
        if (l.job_id && !map[l.job_id]) map[l.job_id] = l;
      }
      setLettersByJob(map);
    }).catch(() => {});
  }, [jobs]);

  const downloadDoc = async (letter, kind) => {
    setDlBusy(`${kind}-${letter.id}`);
    try {
      const safe = (letter.company || "job").replace(/[^A-Za-z0-9_-]+/g, "_");
      let res, name;
      if (kind === "resume") {
        // Pass the letter_id so the backend can auto-align the CV
        // "professional title" with this vacancy's job_title on-the-fly
        // — no need to have run Match-Verbeter first. The stored CV is
        // never mutated by this hint.
        res = await api.get(`/resumes/${letter.resume_id}/pdf?lang=${letter.lang}&letter_id=${letter.id}`, { responseType: "blob" });
        name = `cv_${safe}_${letter.lang}.pdf`;
      } else if (kind === "package") {
        res = await api.get(`/letters/${letter.id}/package`, { responseType: "blob" });
        name = `${letter.lang === "nl" ? "sollicitatiepakket" : "application_package"}_${safe}.pdf`;
      } else {
        res = await api.get(`/letters/${letter.id}/pdf?doc=${kind}`, { responseType: "blob" });
        const prefix = kind === "validation"
          ? (letter.lang === "nl" ? "eisen_validatie" : "requirement_validation")
          : (letter.lang === "nl" ? "motivatiebrief" : "motivation_letter");
        name = `${prefix}_${safe}.pdf`;
      }
      downloadBlob(new Blob([res.data], { type: "application/pdf" }), name);
    } catch (err) {
      toast.error(err?.response?.status === 404 && kind === "resume" ? t("ltDlResumeMissing") : t("rbPdfFailed"));
    } finally {
      setDlBusy(null);
    }
  };

  // Bulk match-insight generation state — kept local so the button flips
  // to a spinner while the (potentially long) LLM batch is in flight and
  // we can surface a toast with the produced/skipped counts.
  const [bulkBusy, setBulkBusy] = useState(false);
  const handleBulkInsights = async () => {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/jobs/match-insight/bulk?limit=15");
      const parts = [t("dashBulkInsightsDone").replace("{n}", String(data.generated))];
      if (data.remaining > 0) parts.push(t("dashBulkInsightsMore").replace("{n}", String(data.remaining)));
      if (data.strong_matches_alerted > 0) parts.push(t("dashBulkInsightsAlerts").replace("{n}", String(data.strong_matches_alerted)));
      toast.success(parts.join(" · "));
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dashBulkInsightsFailed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = jobs.filter((j) => {
    if (filterStatus !== "all" && j.status !== filterStatus) return false;
    if (filterChannel !== "all" && j.channel !== filterChannel) return false;
    if (filterType !== "all" && j.job_type !== filterType) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!j.job_title.toLowerCase().includes(s) && !j.company.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  // Sort — vacatures met een AI-match-inzicht bovenaan (`match_desc`) of
  // onderaan (`match_asc`) op basis van `match_insight.score`. Rijen zonder
  // insight worden altijd naar het einde geduwd zodat het overzicht niet
  // vertekend raakt door lege scores. `recent` = originele volgorde
  // (date_applied desc uit de backend), `oldest` draait die om.
  const sorted = (() => {
    if (sortBy === "recent") return filtered;
    if (sortBy === "oldest") return [...filtered].reverse();
    const dir = sortBy === "match_asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const sa = a.match_insight?.score;
      const sb = b.match_insight?.score;
      const missA = sa === undefined || sa === null;
      const missB = sb === undefined || sb === null;
      if (missA && missB) return 0;
      if (missA) return 1;   // A geen score → naar het einde
      if (missB) return -1;  // B geen score → naar het einde
      return (sa - sb) * dir;
    });
  })();

  const counts = {
    all: jobs.length,
    saved: jobs.filter((j) => j.status === "saved").length,
    applied: jobs.filter((j) => j.status === "applied").length,
    invited: jobs.filter((j) => j.status === "invited").length,
    clarification: jobs.filter((j) => j.status === "clarification").length,
    rejected: jobs.filter((j) => j.status === "rejected").length,
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  };

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const pageIds = paged.map((j) => j.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someOnPageSelected = pageIds.some((id) => selectedIds.has(id));
  const togglePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectedCount = selectedIds.size;
  const runBulkArchive = async () => {
    if (jobsView === "archive") {
      await onBulkUnarchive?.(Array.from(selectedIds));
    } else {
      await onBulkArchive?.(Array.from(selectedIds));
    }
    clearSelection();
  };

  return (
    <div data-testid="jobs-overview">
      <section className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <p className="ed-eyebrow" data-testid="dashboard-subtitle">{t("dashSubtitle")}</p>
          <h1 className="ed-h1 mt-4">{t("dashOverviewTitle")}</h1>
          <p className="font-mono text-xs text-neutral-500 mt-3" data-testid="dashboard-count">
            <span className="serif-italic text-base">{counts.all}</span>{" "}
            {counts.all === 1 ? t("dashHeading1") : t("dashHeadingN")}
          </p>
          <div className="ed-rule w-24 mt-8" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {jobsView === "active" && (
            <button
              data-testid="bulk-insights-btn"
              onClick={handleBulkInsights}
              disabled={bulkBusy}
              className="ed-btn ed-btn-outline inline-flex items-center gap-2 disabled:opacity-50"
              title={t("dashBulkInsightsHint")}
            >
              <Sparkles size={13} strokeWidth={1.5} className={bulkBusy ? "animate-pulse" : ""} />
              <span>{bulkBusy ? t("dashBulkInsightsBusy") : t("dashBulkInsightsBtn")}</span>
            </button>
          )}
          {bulkBusy && (
            <ThinkingLoader stage="bulk" testId="bulk-insights-thinking" />
          )}
          <button data-testid="add-job-button" onClick={onNewJob} className="ed-btn">
            <Plus size={14} strokeWidth={1.5} />
            <span>{t("njwCta")}</span>
          </button>
        </div>
      </section>

      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-neutral-200 mb-12">
        {STATUS_KEYS.map((k) => (
          <div key={k} className="bg-[#F9F8F6] p-6" data-testid={`status-stat-${k}`}>
            <p className="ed-eyebrow">{t(statusLabelKey(k))}</p>
            <p className="font-serif text-4xl mt-2">{counts[k]}</p>
          </div>
        ))}
      </section>

      {onGoToJobSearch && (
        <section
          className="ed-card p-6 mb-12 flex flex-wrap items-center gap-5 justify-between"
          data-testid="jobsearch-widget"
        >
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="w-11 h-11 shrink-0 rounded-full bg-neutral-900 text-[#F9F8F6] flex items-center justify-center">
              <Compass size={18} strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <p className="ed-eyebrow">{t("jsWidgetEyebrow")}</p>
              <h3 className="font-serif text-xl leading-tight mt-1">{t("jsWidgetTitle")}</h3>
              <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{t("jsWidgetBody")}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {PROMOTED_SOURCES.map((s) => (
                  <span
                    key={s.key}
                    className="inline-flex items-center font-mono text-[0.55rem] uppercase tracking-[0.12em] text-white rounded-full px-2 py-0.5"
                    style={{ backgroundColor: s.color }}
                  >
                    {s.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <button
            data-testid="jobsearch-widget-cta"
            onClick={onGoToJobSearch}
            className="ed-btn inline-flex items-center gap-2 shrink-0"
          >
            {t("jsWidgetCta")} <ArrowRight size={13} strokeWidth={1.75} />
          </button>
        </section>
      )}

      <section className="flex flex-wrap items-center gap-3 mb-4" data-testid="jobs-view-toggle">
        {/* Active vs Archive segmented control. Clicking "Archief" for the
            first time opens the retention-notice popup — subsequent clicks
            navigate silently. Persisted per browser via localStorage. */}
        <div className="inline-flex border border-neutral-300 bg-white">
          <button
            type="button"
            data-testid="jobs-view-active"
            onClick={() => onChangeView && onChangeView("active")}
            className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${
              jobsView === "active" ? "bg-neutral-900 text-[#F9F8F6]" : "text-neutral-700 hover:bg-neutral-50"
            }`}
          >
            {t("dashViewActive")}
          </button>
          <button
            type="button"
            data-testid="jobs-view-archive"
            onClick={() => {
              const seen = window.localStorage.getItem("archiveInfoSeen") === "1";
              if (!seen) {
                setArchiveNotice(true);
              } else {
                onChangeView && onChangeView("archive");
              }
            }}
            className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] border-l border-neutral-300 transition-colors ${
              jobsView === "archive" ? "bg-neutral-900 text-[#F9F8F6]" : "text-neutral-700 hover:bg-neutral-50"
            }`}
          >
            <Archive size={11} strokeWidth={1.75} />
            {t("dashViewArchive")}
          </button>
        </div>
        {jobsView === "archive" && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-mono text-[0.65rem] text-neutral-500" data-testid="jobs-view-archive-hint">
              {t("dashArchiveRetentionShort")}
            </p>
            <div className="flex items-center gap-1 border border-neutral-300 rounded-full overflow-hidden">
              <button
                type="button"
                data-testid="jobs-archive-export-pdf"
                onClick={() => handleExportArchive("pdf")}
                disabled={exportBusy !== null}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-700 hover:bg-neutral-900 hover:text-[#F9F8F6] transition-colors disabled:opacity-50 disabled:cursor-default"
                title={t("dashArchiveExportPdfHint")}
              >
                <FileText size={12} strokeWidth={1.5} />
                {exportBusy === "pdf" ? t("dashArchiveExporting") : "PDF"}
              </button>
              <span className="w-px h-4 bg-neutral-300" />
              <button
                type="button"
                data-testid="jobs-archive-export-xlsx"
                onClick={() => handleExportArchive("xlsx")}
                disabled={exportBusy !== null}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-700 hover:bg-neutral-900 hover:text-[#F9F8F6] transition-colors disabled:opacity-50 disabled:cursor-default"
                title={t("dashArchiveExportXlsxHint")}
              >
                <FileSpreadsheet size={12} strokeWidth={1.5} />
                {exportBusy === "xlsx" ? t("dashArchiveExporting") : "Excel"}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* First-time retention notice modal. Once accepted, the archive
          opens without further prompts. */}
      {archiveNotice && (
        <div
          className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setArchiveNotice(false); }}
          data-testid="archive-notice-modal"
        >
          <div className="bg-white max-w-md w-full p-6 shadow-2xl">
            <p className="ed-eyebrow flex items-center gap-2">
              <Archive size={12} strokeWidth={1.5} /> {t("dashArchiveNoticeEyebrow")}
            </p>
            <h3 className="font-serif text-xl mt-2 leading-tight">{t("dashArchiveNoticeTitle")}</h3>
            <p className="text-sm text-neutral-600 mt-3 leading-relaxed">
              {t("dashArchiveNoticeBody")}
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                data-testid="archive-notice-cancel"
                onClick={() => setArchiveNotice(false)}
                className="ed-btn ed-btn-outline"
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                data-testid="archive-notice-accept"
                onClick={() => {
                  window.localStorage.setItem("archiveInfoSeen", "1");
                  setArchiveNotice(false);
                  onChangeView && onChangeView("archive");
                }}
                className="ed-btn"
              >
                {t("dashArchiveNoticeAccept")}
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
        <div className="md:col-span-5 relative">
          <Search size={16} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            data-testid="search-input"
            type="text"
            placeholder={t("dashSearch")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ed-input pl-7"
          />
        </div>
        <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="ed-label">{t("dashLabelStatus")}</label>
            <select data-testid="filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="ed-input">
              <option value="all">{t("all")} ({counts.all})</option>
              {STATUS_KEYS.map((k) => <option key={k} value={k}>{t(statusLabelKey(k))} ({counts[k]})</option>)}
            </select>
          </div>
          <div>
            <label className="ed-label">{t("dashLabelChannel")}</label>
            <select data-testid="filter-channel" value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)} className="ed-input">
              <option value="all">{t("all")}</option>
              {CHANNEL_KEYS.map((k) => <option key={k} value={k}>{t(channelLabelKey(k))}</option>)}
            </select>
          </div>
          <div>
            <label className="ed-label">{t("dashLabelType")}</label>
            <select data-testid="filter-type" value={filterType} onChange={(e) => setFilterType(e.target.value)} className="ed-input">
              <option value="all">{t("all")}</option>
              {JOB_TYPE_KEYS.map((k) => <option key={k} value={k}>{t(jobTypeLabelKey(k))}</option>)}
            </select>
          </div>
          <div>
            <label className="ed-label">{t("dashLabelSort")}</label>
            <select
              data-testid="filter-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="ed-input"
            >
              <option value="recent">{t("dashSortRecent")}</option>
              <option value="oldest">{t("dashSortOldest")}</option>
              <option value="match_desc">{t("dashSortMatchDesc")}</option>
              <option value="match_asc">{t("dashSortMatchAsc")}</option>
            </select>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="ed-card p-16 text-center">
          <p className="ed-eyebrow">{t("dashLoading")}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="ed-card p-20 text-center" data-testid="empty-state">
          <FileX2 size={48} strokeWidth={1.2} className="mx-auto text-neutral-400" />
          <h2 className="ed-h2 mt-6">
            {jobs.length === 0 ? (
              <>{t("dashEmptyTitleBlank1")} <span className="serif-italic">{t("dashEmptyTitleBlank2")}</span>.</>
            ) : (
              <>{t("dashEmptyTitleNo1")} <span className="serif-italic">{t("dashEmptyTitleNo2")}</span>.</>
            )}
          </h2>
          <p className="text-sm text-neutral-600 mt-3 max-w-md mx-auto leading-relaxed">
            {jobs.length === 0 ? t("dashEmptyBlankBody") : t("dashEmptyNoBody")}
          </p>
          {jobs.length === 0 && (
            <button data-testid="empty-add-job-button" onClick={onNewJob} className="ed-btn mt-8">
              <Plus size={14} strokeWidth={1.5} /> <span>{t("njwCta")}</span>
            </button>
          )}
        </div>
      ) : (
        <div className="ed-card overflow-x-auto" data-testid="jobs-table">
          {selectedCount > 0 && (
            <div
              className="flex items-center justify-between gap-4 px-5 py-3 bg-neutral-900 text-[#F9F8F6] sticky top-0 z-20"
              data-testid="jobs-bulk-bar"
            >
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em]">
                <span className="serif-italic normal-case tracking-normal text-base mr-2">{selectedCount}</span>
                {selectedCount === 1 ? t("dashBulkSelectedOne") : t("dashBulkSelectedN")}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="jobs-bulk-archive"
                  onClick={runBulkArchive}
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#F9F8F6] font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:bg-[#F9F8F6] hover:text-neutral-900 transition-colors"
                >
                  {jobsView === "archive" ? (
                    <><ArchiveRestore size={13} strokeWidth={1.5} /> {t("dashBulkUnarchiveCta")}</>
                  ) : (
                    <><Archive size={13} strokeWidth={1.5} /> {t("dashBulkArchiveCta")}</>
                  )}
                </button>
                <button
                  type="button"
                  data-testid="jobs-bulk-clear"
                  onClick={clearSelection}
                  className="p-1.5 border border-[#F9F8F6] hover:bg-[#F9F8F6] hover:text-neutral-900 transition-colors"
                  title={t("dashBulkClear")}
                >
                  <X size={13} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          )}
          <table className="ed-table">
            <thead>
              <tr>
                <th className="w-8">
                  <button
                    type="button"
                    data-testid="jobs-select-all"
                    onClick={togglePage}
                    className="p-1 -m-1 text-neutral-500 hover:text-neutral-900 transition-colors"
                    title={allOnPageSelected ? t("dashBulkDeselectPage") : t("dashBulkSelectPage")}
                  >
                    {allOnPageSelected ? (
                      <CheckSquare size={15} strokeWidth={1.5} className="text-neutral-900" />
                    ) : someOnPageSelected ? (
                      <CheckSquare size={15} strokeWidth={1.5} className="text-neutral-500" />
                    ) : (
                      <Square size={15} strokeWidth={1.5} />
                    )}
                  </button>
                </th>
                <th>{t("dashColPosition")}</th>
                <th>{t("dashColCompany")}</th>
                <th className="text-center whitespace-nowrap" title={t("dashColMatchHint")}>{t("dashColMatch")}</th>
                <th>{t("dashColStatus")}</th>
                <th>{t("dashColDate")}</th>
                <th>{t("dashColChannel")}</th>
                <th>{t("dashColContract")}</th>
                <th className="text-right">{t("dashColActions")}</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((job, idx) => {
                const badge = statusBadgeClass[job.status] || "badge-applied";
                const docLetter = lettersByJob[job.id];
                const fuDays = job.status === "applied" && job.followed_up_at ? daysSince(job.followed_up_at) : null;
                const stale = job.status === "applied"
                  ? daysSince(job.followed_up_at || job.date_applied)
                  : null;
                const showFollowedUp = fuDays !== null && fuDays < 7;
                return (
                  <tr
                    key={job.id}
                    data-testid={`job-row-${idx}`}
                    className={`cursor-pointer ${selectedIds.has(job.id) ? "bg-neutral-50" : ""}`}
                    onClick={() => onOpenJob(job.id)}
                  >
                    <td className="w-8" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        data-testid={`job-select-${idx}`}
                        onClick={() => toggleOne(job.id)}
                        className="p-1 -m-1 text-neutral-500 hover:text-neutral-900 transition-colors"
                        title={selectedIds.has(job.id) ? t("dashBulkDeselect") : t("dashBulkSelect")}
                      >
                        {selectedIds.has(job.id) ? (
                          <CheckSquare size={15} strokeWidth={1.5} className="text-neutral-900" />
                        ) : (
                          <Square size={15} strokeWidth={1.5} />
                        )}
                      </button>
                    </td>
                    <td className="font-serif text-lg leading-tight">{job.job_title}</td>
                    <td className="text-sm text-neutral-700">{job.company}</td>
                    <td className="text-center whitespace-nowrap">
                      {(() => {
                        const mi = job.match_insight;
                        if (!mi || typeof mi.score !== "number") {
                          return (
                            <span
                              data-testid={`job-match-empty-${idx}`}
                              className="font-mono text-[0.55rem] text-neutral-400 uppercase tracking-[0.1em]"
                              title={t("dashMatchNotYet")}
                            >
                              —
                            </span>
                          );
                        }
                        const s = mi.score;
                        const cls =
                          s >= 80 ? "bg-emerald-700 text-[#F9F8F6]" :
                          s >= 60 ? "bg-lime-700 text-[#F9F8F6]" :
                          s >= 40 ? "bg-amber-600 text-[#F9F8F6]" :
                                    "bg-red-600 text-[#F9F8F6]";
                        return (
                          <span
                            data-testid={`job-match-score-${idx}`}
                            className={`inline-block font-mono text-[0.65rem] font-bold px-2 py-0.5 ${cls}`}
                            title={mi.summary || t("dashMatchNotYet")}
                          >
                            {s}
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        <span className={`ed-badge ${badge}`}>{t(statusLabelKey(job.status))}</span>
                        {showFollowedUp ? (
                          <button
                            data-testid={`job-followed-up-${idx}`}
                            onClick={(e) => { e.stopPropagation(); setFollowUpJob(job); }}
                            title={t("fuTitle")}
                            className="font-mono text-[0.6rem] uppercase tracking-[0.12em] rounded-full border border-emerald-700 text-emerald-700 bg-emerald-50 px-2.5 py-0.5 cursor-pointer hover:bg-emerald-700 hover:text-white transition-colors"
                          >
                            {t("dashFollowedUp")}
                          </button>
                        ) : stale !== null && stale >= 7 && (
                          <button
                            data-testid={`job-stale-${idx}`}
                            onClick={(e) => { e.stopPropagation(); onEmail(job); }}
                            title={t("emwTypeFollowup")}
                            className={`font-mono text-[0.6rem] uppercase tracking-[0.12em] rounded-full border px-2.5 py-0.5 cursor-pointer transition-colors ${
                              stale >= 14
                                ? "border-rose-700 text-rose-700 bg-rose-50 hover:bg-rose-700 hover:text-white"
                                : "border-amber-600 text-amber-700 bg-amber-50 hover:bg-amber-600 hover:text-white"
                            }`}
                          >
                            {t("dashNoResponse", { days: stale })}
                          </button>
                        )}
                      </span>
                    </td>
                    <td className="text-sm font-mono text-neutral-700">{formatDate(job.date_applied, lang)}</td>
                    <td className="text-sm text-neutral-700">
                      {job.channel === "linkedin" && t("channelLinkedIn")}
                      {job.channel === "recruiter" && (
                        <>{t("channelRecruiter")}{job.recruiter_name ? <> · <span className="serif-italic">{job.recruiter_name}</span></> : null}</>
                      )}
                      {job.channel === "open" && t("channelOpen")}
                    </td>
                    <td className="text-sm">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] px-2 py-0.5"
                          data-testid={`job-type-badge-${idx}`}
                          title={t(jobTypeLabelKey(job.job_type))}
                        >
                          {jobTypeShortLabel(job.job_type)}
                        </span>
                        {job.hours_per_week ? (
                          <span className="font-mono text-xs text-neutral-700" data-testid={`job-hours-${idx}`}>
                            {job.hours_per_week}h/w
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                        <div className="relative">
                          <button
                            data-testid={`actions-menu-${idx}`}
                            onClick={() => openDocs(job.id)}
                            className={`p-1.5 border transition-colors ${docsOpen === job.id ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"}`}
                            title={t("dashActionsMenu")}
                            aria-expanded={docsOpen === job.id}
                            aria-haspopup="true"
                          >
                            <MoreVertical size={13} strokeWidth={1.5} />
                          </button>
                          {docsOpen === job.id && (
                            <>
                              <div className="fixed inset-0 z-30" onClick={() => setDocsOpen(null)} />
                              <div
                                className="absolute right-0 top-full mt-1 z-40 w-80 border border-neutral-900 bg-white shadow-xl text-left overflow-visible"
                                data-testid={`actions-menu-panel-${idx}`}
                              >
                                <p className="px-4 pt-3 ed-eyebrow">{t("dashActionsMenu")}</p>
                                <div className="px-2 py-2 flex flex-col gap-1">
                                  {job.status === "saved" && (
                                    <button
                                      data-testid={`quick-apply-job-${idx}`}
                                      onClick={() => { setDocsOpen(null); onQuickApply && onQuickApply(job); }}
                                      className="flex items-center gap-2 px-3 py-2 font-mono text-[0.6rem] leading-tight uppercase tracking-[0.12em] border border-emerald-600 bg-emerald-50 text-emerald-800 hover:bg-emerald-600 hover:text-white transition-colors text-left"
                                      title={t("dashQuickApply")}
                                    >
                                      <Rocket size={12} strokeWidth={1.7} className="shrink-0" /> <span className="whitespace-nowrap">{t("dashQuickApply")}</span>
                                    </button>
                                  )}
                                  <button
                                    data-testid={`followup-job-${idx}`}
                                    onClick={() => { setDocsOpen(null); setFollowUpJob(job); }}
                                    className="flex items-center gap-2 px-3 py-2 font-mono text-[0.6rem] leading-tight uppercase tracking-[0.12em] border border-neutral-300 hover:border-emerald-700 hover:bg-emerald-700 hover:text-white transition-colors text-left"
                                    title={t("fuTitle")}
                                  >
                                    <CalendarCheck size={12} strokeWidth={1.5} className="shrink-0" /> <span className="whitespace-nowrap">{t("fuTitle")}</span>
                                  </button>
                                  <button
                                    data-testid={`letter-job-${idx}`}
                                    onClick={() => { setDocsOpen(null); onLetter(job); }}
                                    className="flex items-center gap-2 px-3 py-2 font-mono text-[0.6rem] leading-tight uppercase tracking-[0.12em] border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors text-left"
                                    title={t("dashLetterButton")}
                                  >
                                    <Mail size={12} strokeWidth={1.5} className="shrink-0" /> <span className="whitespace-nowrap">{t("dashLetterButton")}</span>
                                  </button>
                                  <button
                                    data-testid={`email-job-${idx}`}
                                    onClick={() => { setDocsOpen(null); onEmail(job); }}
                                    className="flex items-center gap-2 px-3 py-2 font-mono text-[0.6rem] leading-tight uppercase tracking-[0.12em] border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors text-left"
                                    title={t("emwTitle")}
                                  >
                                    <Send size={12} strokeWidth={1.5} className="shrink-0" /> <span className="whitespace-nowrap">{t("emwTitle")}</span>
                                  </button>
                                  <button
                                    data-testid={`prep-job-${idx}`}
                                    onClick={() => { setDocsOpen(null); onPrep(job); }}
                                    className="flex items-center gap-2 px-3 py-2 font-mono text-[0.6rem] leading-tight uppercase tracking-[0.12em] border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors text-left"
                                    title={t("prepTitle")}
                                  >
                                    <Sparkles size={12} strokeWidth={1.5} className="shrink-0" /> <span className="whitespace-nowrap">{t("prepTitle")}</span>
                                  </button>
                                  {jobsView === "archive" ? (
                                    <button
                                      data-testid={`unarchive-job-${idx}`}
                                      onClick={() => { setDocsOpen(null); onUnarchive && onUnarchive(job.id); }}
                                      className="flex items-center gap-2 px-3 py-2 font-mono text-[0.6rem] leading-tight uppercase tracking-[0.12em] border border-neutral-300 hover:border-emerald-700 hover:bg-emerald-700 hover:text-white transition-colors text-left"
                                      title={t("dashUnarchive")}
                                    >
                                      <ArchiveRestore size={12} strokeWidth={1.5} className="shrink-0" /> <span className="whitespace-nowrap">{t("dashUnarchive")}</span>
                                    </button>
                                  ) : (
                                    <button
                                      data-testid={`archive-job-${idx}`}
                                      onClick={() => { setDocsOpen(null); onArchive && onArchive(job.id); }}
                                      className="flex items-center gap-2 px-3 py-2 font-mono text-[0.6rem] leading-tight uppercase tracking-[0.12em] border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors text-left"
                                      title={t("dashArchive")}
                                    >
                                      <Archive size={12} strokeWidth={1.5} className="shrink-0" /> <span className="whitespace-nowrap">{t("dashArchive")}</span>
                                    </button>
                                  )}
                                  <button
                                    data-testid={`delete-job-${idx}`}
                                    onClick={() => { setDocsOpen(null); onDelete(job.id); }}
                                    className="flex items-center gap-2 px-3 py-2 font-mono text-[0.6rem] leading-tight uppercase tracking-[0.12em] border border-neutral-300 hover:border-rose-700 hover:bg-rose-700 hover:text-white transition-colors text-left"
                                    title={t("delete")}
                                  >
                                    <Trash2 size={12} strokeWidth={1.5} className="shrink-0" /> <span className="whitespace-nowrap">{t("delete")}</span>
                                  </button>
                                </div>
                                {(docsCache[job.id]?.cv?.present || docLetter) && (
                                  <>
                                    <div className="border-t border-neutral-200" />
                                    <p className="px-4 pt-3 ed-eyebrow">{t("dashDocsTitle")}</p>
                                    {docsCache[job.id]?.cv?.present && (
                                      <>
                                        <p className="px-4 py-2 font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500">{t("dashDocsSectionUploaded")}</p>
                                        <button
                                          data-testid={`docs-dl-jobcv-${idx}`}
                                          onClick={() => downloadJobCv(job.id)}
                                          disabled={!!dlBusy}
                                          className="w-full flex items-center justify-between gap-3 px-4 py-3 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-50 whitespace-nowrap"
                                        >
                                          <span className="truncate flex items-center gap-2"><Paperclip size={11} strokeWidth={1.5} />{docsCache[job.id].cv.filename || t("dashDocsJobCv")}</span>
                                          <FileDown size={13} strokeWidth={1.5} className={dlBusy === `jobcv-${job.id}` ? "animate-pulse" : ""} />
                                        </button>
                                      </>
                                    )}
                                    {docLetter && (
                                      <>
                                        <p className="px-4 py-2 border-t border-neutral-200 font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500">
                                          {t("dashDocsSectionLetter")} · <span className="italic normal-case tracking-normal">"{docLetter.resume_name}"</span>
                                        </p>
                                        {[
                                          ["resume", t("dashDocsCv")],
                                          ["letter", t("dashDocsLetter")],
                                          ["validation", t("dashDocsValidation")],
                                          ["package", t("dashDocsPackage")],
                                        ].map(([kind, label]) => (
                                          <button
                                            key={kind}
                                            data-testid={`docs-dl-${kind}-${idx}`}
                                            onClick={() => downloadDoc(docLetter, kind)}
                                            disabled={!!dlBusy}
                                            className="w-full flex items-center justify-between gap-3 px-4 py-3 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-50 whitespace-nowrap"
                                          >
                                            {label}
                                            <FileDown size={13} strokeWidth={1.5} className={dlBusy === `${kind}-${docLetter.id}` ? "animate-pulse" : ""} />
                                          </button>
                                        ))}
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-t border-neutral-200" data-testid="jobs-pagination">
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500">
                {t("pageInfo", { page: safePage, total: totalPages })}
              </p>
              <div className="flex items-center gap-1.5">
                <button
                  data-testid="page-prev"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none"
                  title={t("pagePrev")}
                >
                  <ChevronLeft size={14} strokeWidth={1.5} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    data-testid={`page-${p}`}
                    onClick={() => setPage(p)}
                    className={`min-w-[2.1rem] px-2 py-1.5 border font-mono text-xs transition-colors ${
                      p === safePage
                        ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]"
                        : "border-neutral-300 hover:border-neutral-900"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  data-testid="page-next"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none"
                  title={t("pageNext")}
                >
                  <ChevronRight size={14} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {followUpJob && (
        <FollowUpDialog
          job={followUpJob}
          onClose={() => setFollowUpJob(null)}
          onSaved={() => onRefresh && onRefresh()}
        />
      )}
    </div>
  );
}
